import { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { useAccessApi, Organization, EnhancedAuth0User, TeamMembership } from '../services/api';
import { InlineSpinner, LoadingState } from './LoadingSpinner';
import {
    useUpdateUserTeamRoleOrgAdmin,
    useRemoveUserFromTeamOrgAdmin,
    useSyncUserData,
} from '../hooks/useApi';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from './ui/table';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { RoleBadge } from './RoleBadge';
import { Button } from './ui/button';
import { ConfirmDialog } from './ui/confirm-dialog';
import { DialogFooter, DialogFrame } from './ui/dialog-frame';
import { InlineAlert } from './ui/inline-alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { overlineClassName, pageTitleClassName } from './ui/typography';
import { cn, pluralize } from '../lib/utils';
import {
    Check,
    ChevronRight,
    CircleCheck,
    CircleDashed,
    Clock,
    Info,
    RefreshCw,
    ShieldCheck,
    Trash2,
    TriangleAlert,
    UserMinus,
    Users,
} from 'lucide-react';
import {
    ROLE_TOGGLE_ACTIVE_CLASS,
    ROLE_TOGGLE_BASE_CLASS,
    ROLE_TOGGLE_GROUP_CLASS,
    ROLE_TOGGLE_INACTIVE_CLASS,
} from './admin/roleToggleStyles';
import { ADMIN_BADGE_BASE_CLASS, ADMIN_BADGE_NEUTRAL_CLASS } from './admin/credit/adminStatusBadge';
import { AdminToast } from './admin/AdminToast';
import {
    clearAdminToastTimer,
    emptyAdminToast,
    type AdminToastState,
} from './admin/adminToastTypes';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getProRoleName } from './admin/teamRoleLabels';

// Helper function to render user avatar
const renderUserAvatar = (name: string, hasNotLoggedIn = false) => (
    <div
        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            hasNotLoggedIn ? 'bg-neutral-100' : 'bg-neutral-200'
        }`}
    >
        <span className="text-xs font-medium text-neutral-600">
            {name
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()}
        </span>
    </div>
);

type TeamMemberUser = EnhancedAuth0User & {
    current_team_role_id?: number;
    current_team_role_name?: string;
};

const PRO_TEAM_ROLE_BUTTONS = [
    {
        roleId: 1,
        label: 'Restricted',
        title: 'Set as Pro Restricted',
    },
    {
        roleId: 2,
        label: 'User',
        title: 'Set as Pro User',
    },
    {
        roleId: 3,
        label: 'Approver',
        title: 'Set as Pro Approver',
    },
] as const;

type TeamMemberRoleButtonProps = {
    teamId: number;
    user: TeamMemberUser;
    roleId: number;
    label: string;
    title: string;
    loadingStates: Record<string, boolean>;
    getMemberLoadingKey: (
        member: { subject_id?: string | null; user_id?: string },
        action: string,
        roleId?: number,
    ) => string;
    onSetRole: (teamId: number, userId: string, newRoleId: number) => void;
    hasNotLoggedIn: boolean;
};

function ActiveTeamRoleIcon({ isActive }: { isActive: boolean }) {
    return <Check className={cn('size-3 shrink-0', !isActive && 'invisible')} aria-hidden />;
}

function getAriaBusy(isLoading: boolean): true | undefined {
    return isLoading ? true : undefined;
}

function getTeamMemberUserId(user: TeamMemberUser): string {
    return (user.subject_id || user.user_id) as string;
}

function isTeamMemberRoleButtonDisabled(isLoading: boolean, hasNotLoggedIn: boolean): boolean {
    return isLoading || hasNotLoggedIn;
}

function TeamMemberRoleButton({
    teamId,
    user,
    roleId,
    label,
    title,
    loadingStates,
    getMemberLoadingKey,
    onSetRole,
    hasNotLoggedIn,
}: TeamMemberRoleButtonProps) {
    const userId = getTeamMemberUserId(user);
    const isActive = user.current_team_role_id === roleId;
    const loadingKey = getMemberLoadingKey(user, 'setRole', roleId);
    const isLoading = loadingStates[loadingKey];

    return (
        <button
            type="button"
            aria-pressed={isActive}
            aria-busy={getAriaBusy(isLoading)}
            onClick={(e) => {
                e.preventDefault();
                onSetRole(teamId, userId, roleId);
                e.stopPropagation();
            }}
            disabled={isTeamMemberRoleButtonDisabled(isLoading, hasNotLoggedIn)}
            className={cn(
                ROLE_TOGGLE_BASE_CLASS,
                isActive ? ROLE_TOGGLE_ACTIVE_CLASS : ROLE_TOGGLE_INACTIVE_CLASS,
            )}
            title={hasNotLoggedIn ? 'User not synced - cannot change role' : title}
        >
            {isLoading ? (
                <>
                    <InlineSpinner className="size-3" label={null} />
                    {label}
                </>
            ) : (
                <>
                    <ActiveTeamRoleIcon isActive={isActive} />
                    {label}
                </>
            )}
        </button>
    );
}

function toNumber(value: unknown): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
}

interface TeamWithMembers {
    id: number;
    name: string;
    members: TeamMemberUser[];
}

type OrgTeamsConfirm =
    | {
          kind: 'remove-from-team';
          teamId: number;
          teamName: string;
          userId: string;
          userName: string;
      }
    | { kind: 'delete-user'; userId: string; userLabel: string }
    | { kind: 'register-to-team'; count: number }
    | { kind: 'register-user'; userId: string; userLabel: string };

export function OrganizationTeamsView() {
    const { orgId } = useParams<{ orgId: string }>();
    const { hasPermission } = useUserPermissions();
    const canManageUsers = hasPermission('manage:users');
    const {
        getOrganizations,
        getOrganizationDatabaseUsers,
        syncAllUnsyncedUsersSSE,
        deactivateUser,
    } = useAccessApi();
    const navigate = useNavigate();
    const [organization, setOrganization] = useState<Organization | null>(null);
    useDocumentTitle(organization?.display_name || organization?.name || 'Organization');
    const [orgUsers, setOrgUsers] = useState<EnhancedAuth0User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [expandedTeams, setExpandedTeams] = useState<Set<number>>(new Set());
    const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});
    const [syncingDataUsers, setSyncingDataUsers] = useState<Set<string>>(new Set());
    const [isRefreshingAfterSync, setIsRefreshingAfterSync] = useState(false);
    const [toast, setToast] = useState<AdminToastState>(emptyAdminToast);
    const [confirmAction, setConfirmAction] = useState<OrgTeamsConfirm | null>(null);
    const toastTimerRef = useRef<number | null>(null);
    const [showBulkRegisterModal, setShowBulkRegisterModal] = useState(false);
    const [bulkRegisterStatus, setBulkRegisterStatus] = useState<{
        isRunning: boolean;
        totalUsers: number;
        registeredUsers: number;
        currentUser: string | null;
        errors: string[];
    }>({
        isRunning: false,
        totalUsers: 0,
        registeredUsers: 0,
        currentUser: null,
        errors: [],
    });
    const cancelBulkRegisterRef = useRef<(() => void) | null>(null);

    const clearToastTimer = useCallback(() => clearAdminToastTimer(toastTimerRef), []);

    const showToast = (next: AdminToastState) => {
        clearToastTimer();
        setToast(next);
        if (next.type === 'success') {
            toastTimerRef.current = window.setTimeout(() => setToast(emptyAdminToast), 3000);
        }
    };

    useEffect(() => clearToastTimer, [clearToastTimer]);

    const updateUserTeamRole = useUpdateUserTeamRoleOrgAdmin();
    const removeUserFromTeam = useRemoveUserFromTeamOrgAdmin();
    const syncUserDataMutation = useSyncUserData();

    const setLoading = (key: string, loading: boolean) => {
        setLoadingStates((prev) => ({ ...prev, [key]: loading }));
    };

    const getMemberLoadingKey = (
        member: { subject_id?: string | null; user_id?: string },
        action: string,
        roleId?: number,
    ): string => {
        const memberId = (member.subject_id || member.user_id || '').toString();
        return roleId ? `${action}-${memberId}-${roleId}` : `${action}-${memberId}`;
    };

    // Fetch organization details and users
    useEffect(() => {
        const fetchData = async () => {
            if (!orgId) return;

            try {
                setIsLoading(true);
                setError(null);

                // Get organization details
                const orgs = await getOrganizations();
                const org = orgs.find((o) => o.id === orgId);
                if (!org) {
                    setError('Organization not found.');
                    return;
                }
                setOrganization(org);

                // Get organization users
                const response = await getOrganizationDatabaseUsers(orgId, true);

                setOrgUsers(response.users);
            } catch (err) {
                console.error('Error fetching organization data:', err);
                setError('Failed to load organization data.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

    const refreshOrgUsers = async () => {
        if (!orgId) {
            console.warn('No orgId available for refresh');
            return;
        }
        try {
            console.log('Fetching updated organization users...');
            const response = await getOrganizationDatabaseUsers(orgId, true);
            console.log('Received updated users:', response.users.length, 'users');
            setOrgUsers(response.users);
        } catch (err) {
            console.error('Error refreshing organization users:', err);
            throw err; // Re-throw to allow caller to handle
        }
    };

    // Group users by teams
    const teamsWithMembers = useMemo((): TeamWithMembers[] => {
        const teamMap = new Map<number, TeamWithMembers>();
        let emptyTeamId: number | null = null;

        // Process each user and their team memberships
        orgUsers.forEach((user) => {
            if (user.team_memberships && user.team_memberships.length > 0) {
                user.team_memberships.forEach((membership: TeamMembership) => {
                    if (!teamMap.has(membership.team_id)) {
                        teamMap.set(membership.team_id, {
                            id: membership.team_id,
                            name: membership.team_name,
                            members: [],
                        });
                        // Track empty team ID
                        if (!membership.team_name || membership.team_name === '') {
                            emptyTeamId = membership.team_id;
                        }
                    }
                    teamMap.get(membership.team_id)!.members.push({
                        ...user,
                        // Add the specific team role for this membership
                        current_team_role_id: toNumber(
                            (membership as unknown as { team_role_id: string | number })
                                .team_role_id,
                        ),
                        current_team_role_name: membership.team_role_name,
                    } as TeamMemberUser);
                });
            }
        });

        // Always ensure empty team exists if there are any users
        // The empty team should always be shown (it's the "All Users" team)
        if (orgUsers.length > 0 && !emptyTeamId) {
            // Find the first empty team from any user's memberships, or create placeholder
            // We'll use a placeholder ID that indicates it needs to be created
            const placeholderId = -999; // Special ID to indicate empty team placeholder
            if (!teamMap.has(placeholderId)) {
                teamMap.set(placeholderId, {
                    id: placeholderId,
                    name: '',
                    members: [],
                });
            }
        }

        // Sort teams: empty name (All Users) first, then alphabetically
        return Array.from(teamMap.values()).sort((a, b) => {
            // Empty name (All Users) always comes first
            if (!a.name || a.name === '') return -1;
            if (!b.name || b.name === '') return 1;
            return a.name.localeCompare(b.name);
        });
    }, [orgUsers]);

    // Get users not in any teams
    const usersNotInAnyTeams = useMemo(() => {
        if (!orgUsers.length) return [];

        return orgUsers.filter((user: EnhancedAuth0User) => {
            return !user.team_memberships || user.team_memberships.length === 0;
        });
    }, [orgUsers]);

    const toggleTeam = (teamId: number) => {
        setExpandedTeams((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(teamId)) {
                newSet.delete(teamId);
            } else {
                newSet.add(teamId);
            }
            return newSet;
        });
    };

    const handleUserClick = (user: EnhancedAuth0User) => {
        // Only navigate for users with subject_id (synced users)
        if (user.subject_id && orgId) {
            navigate(`/admin/${orgId}/user/${user.subject_id}`);
        }
    };

    const handleSetRole = async (teamId: number, userId: string, newRoleId: number) => {
        if (!orgId) return;
        try {
            const loadingKey = `setRole-${userId}-${newRoleId}`;
            setLoading(loadingKey, true);
            await updateUserTeamRole.mutateAsync({
                orgId,
                teamId: teamId.toString(),
                userId,
                team_role_id: newRoleId,
            });
            await refreshOrgUsers();
        } catch (err) {
            showToast({
                show: true,
                message:
                    err instanceof Error ? err.message : 'Could not change the role. Try again.',
                type: 'error',
            });
        } finally {
            const loadingKey = `setRole-${userId}-${newRoleId}`;
            setLoading(loadingKey, false);
        }
    };

    const handleRemoveUser = (teamId: number, userId: string, userName: string) => {
        if (!orgId) return;

        // Find the team to check if it's the "All Users" team
        const team = teamsWithMembers.find((t) => t.id === teamId);
        if (!team || !team.name || team.name === '') {
            showToast({
                show: true,
                message:
                    'Cannot remove user from "All Users" team. This team represents the required company relationship and all users must be in it.',
                type: 'error',
            });
            return;
        }

        const fullUser = orgUsers.find((u) => u.user_id === userId || u.subject_id === userId);
        if (fullUser?.team_memberships && fullUser.team_memberships.length === 1) {
            showToast({
                show: true,
                message:
                    'Cannot remove user from their only team. Users must be in at least one team.',
                type: 'error',
            });
            return;
        }

        setConfirmAction({
            kind: 'remove-from-team',
            teamId,
            teamName: team.name,
            userId,
            userName,
        });
    };

    const removeUserFromTeamConfirmed = async (teamId: number, userId: string) => {
        if (!orgId) return;
        try {
            setLoading('removeUser', true);
            await removeUserFromTeam.mutateAsync({
                orgId,
                teamId: teamId.toString(),
                userId,
            });
            await refreshOrgUsers();
        } catch (err) {
            showToast({
                show: true,
                message:
                    err instanceof Error
                        ? err.message
                        : 'Could not remove the user from the team. Try again.',
                type: 'error',
            });
        } finally {
            setLoading('removeUser', false);
        }
    };

    // Filter unsynced users (those without subject_id)
    // Exclude users that are legacy, not SSO, and have no password (invalid users)
    // Also exclude SSO users (they will be associated on next SSO login)
    const unsyncedUsers = useMemo(() => {
        return orgUsers.filter((user) => {
            // Must be unsynced (no subject_id)
            if (user.subject_id) return false;

            // Exclude SSO users (they will be associated on next SSO login)
            if (user.is_sso === true) return false;

            // Check if user is invalid (legacy, not SSO, no password)
            const isInvalidUser = !user.is_sso && !user.has_password;

            // Exclude invalid users from registration
            return !isInvalidUser;
        });
    }, [orgUsers]);

    // Find users with subject_id that are not in the empty team
    const usersNeedingEmptyTeamRegistration = useMemo(() => {
        return orgUsers.filter((user) => {
            // Must have subject_id (synced)
            if (!user.subject_id) return false;

            // Check if user is in the empty team
            const inEmptyTeam =
                user.team_memberships?.some((m) => !m.team_name || m.team_name === '') || false;

            return !inEmptyTeam;
        });
    }, [orgUsers]);

    // Helper to check if a user is invalid (should show warning but not be registerable)
    const isInvalidUser = (user: EnhancedAuth0User): boolean => {
        // Must be legacy (no subject_id)
        if (user.subject_id) return false;

        // Invalid if: not SSO AND no password
        return !user.is_sso && !user.has_password;
    };

    const handleBulkRegister = async () => {
        if (!orgId) return;
        setShowBulkRegisterModal(true);
        setBulkRegisterStatus({
            isRunning: false,
            totalUsers: unsyncedUsers.length,
            registeredUsers: 0,
            currentUser: null,
            errors: [],
        });
    };

    const handleRegisterToEmptyTeam = async () => {
        if (!orgId) return;

        try {
            setIsRefreshingAfterSync(true);
            setError(null);

            // Use the same SSE endpoint - it handles both unsynced users and empty team registration
            const cancel = await syncAllUnsyncedUsersSSE(
                orgId,
                (data: unknown) => {
                    // Handle progress updates
                    const message = data as {
                        type: string;
                        message?: string;
                        progress?: number;
                        totalUsers?: number;
                        registeredUsers?: number;
                    };
                    if (message.type === 'team_registration_progress') {
                        console.log('Team registration progress:', message);
                    }
                },
                (err: Error) => {
                    console.error('Error registering to empty team:', err);
                    showToast({
                        show: true,
                        message: `Failed to register users to empty team: ${err.message}`,
                        type: 'error',
                    });
                    setIsRefreshingAfterSync(false);
                },
                async () => {
                    // On complete, refresh the user list
                    try {
                        await refreshOrgUsers();
                    } catch (refreshErr) {
                        console.error('Error refreshing after sync:', refreshErr);
                    } finally {
                        setIsRefreshingAfterSync(false);
                    }
                },
            );

            // Store cancel function
            cancelBulkRegisterRef.current = cancel;
        } catch (err) {
            console.error('Error starting empty team registration:', err);
            showToast({
                show: true,
                message: 'Failed to start registration. Please try again.',
                type: 'error',
            });
            setIsRefreshingAfterSync(false);
        }
    };

    const handleStartBulkRegister = async () => {
        if (!orgId) return;

        setBulkRegisterStatus((prev) => ({
            ...prev,
            isRunning: true,
        }));

        try {
            const cancel = await syncAllUnsyncedUsersSSE(
                orgId,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (data: any) => {
                    switch (data.type) {
                        case 'start':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                totalUsers: data.totalUsers,
                            }));
                            break;

                        case 'user_start':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                currentUser: data.userName || data.userPublicId,
                                registeredUsers: data.progress - 1,
                            }));
                            break;

                        case 'user_complete':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                registeredUsers: data.progress,
                                currentUser: null,
                            }));
                            break;

                        case 'user_error':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                registeredUsers: data.progress,
                                currentUser: null,
                                errors: [
                                    ...prev.errors,
                                    `${data.userName || data.userPublicId}: ${data.error}`,
                                ],
                            }));
                            break;

                        case 'complete':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                isRunning: false,
                                currentUser: null,
                            }));
                            // Refresh the user list
                            refreshOrgUsers();
                            break;

                        case 'error':
                            setBulkRegisterStatus((prev) => ({
                                ...prev,
                                isRunning: false,
                                errors: [...prev.errors, data.error],
                            }));
                            break;
                    }
                },
                (error) => {
                    console.error('Bulk register error:', error);
                    setBulkRegisterStatus((prev) => ({
                        ...prev,
                        isRunning: false,
                        errors: [...prev.errors, error.message],
                    }));
                },
                () => {
                    setBulkRegisterStatus((prev) => ({
                        ...prev,
                        isRunning: false,
                    }));
                },
            );

            cancelBulkRegisterRef.current = cancel;
        } catch (error) {
            console.error('Failed to start bulk register:', error);
            setBulkRegisterStatus((prev) => ({
                ...prev,
                isRunning: false,
                errors: [
                    ...prev.errors,
                    error instanceof Error
                        ? error.message
                        : 'Could not start registration. Close this dialog and try again.',
                ],
            }));
        }
    };

    const handleCancelBulkRegister = () => {
        const currentStatus = bulkRegisterStatus;
        if (cancelBulkRegisterRef.current) {
            cancelBulkRegisterRef.current();
            cancelBulkRegisterRef.current = null;
        }
        setBulkRegisterStatus((prev) => ({
            ...prev,
            isRunning: false,
            currentUser: null,
        }));
        // Refresh users list if any were registered before cancellation
        if (currentStatus.registeredUsers > 0) {
            refreshOrgUsers();
        }
        showToast({
            show: true,
            message: `Registration cancelled. ${currentStatus.registeredUsers} of ${currentStatus.totalUsers} ${pluralize(
                currentStatus.totalUsers,
                'user was',
                'users were',
            )} registered.`,
            type: 'success',
        });
    };

    const handleCloseBulkRegisterModal = () => {
        if (!bulkRegisterStatus.isRunning) {
            // Refresh users list if any were registered
            if (bulkRegisterStatus.registeredUsers > 0) {
                refreshOrgUsers();
            }
            setShowBulkRegisterModal(false);
            setBulkRegisterStatus({
                isRunning: false,
                totalUsers: 0,
                registeredUsers: 0,
                currentUser: null,
                errors: [],
            });
        }
    };

    const handleDeleteUser = (userId: string) => {
        if (!orgId) return;

        const user = orgUsers.find((u) => u.user_id === userId);
        if (!user) return;

        // Only allow deletion of legacy users (users without subject_id)
        if (user.subject_id) {
            showToast({
                show: true,
                message:
                    'Cannot delete users that are already synced to Auth0. Only legacy users can be deleted.',
                type: 'error',
            });
            return;
        }

        // Don't allow deletion of SSO users (they will be associated on next SSO login)
        if (user.is_sso === true) {
            showToast({
                show: true,
                message:
                    'Cannot delete SSO users. They will be associated at their next SSO sign-in.',
                type: 'error',
            });
            return;
        }

        setConfirmAction({
            kind: 'delete-user',
            userId,
            userLabel: user.name || user.email,
        });
    };

    const deleteUserConfirmed = async (userId: string) => {
        if (!orgId) return;
        const user = orgUsers.find((u) => u.user_id === userId);
        if (!user) return;
        try {
            // Extract public_id from user_id (could be 'db|publicId' or just 'publicId')
            const userPublicId = user.public_id || userId.replace('db|', '');

            await deactivateUser(orgId, userPublicId);

            showToast({
                show: true,
                message: `User ${user.name || user.email} has been deleted successfully.`,
                type: 'success',
            });

            // Refresh the user list
            await refreshOrgUsers();
        } catch (error) {
            console.error('Failed to delete user:', error);
            const errorMessage =
                error instanceof Error ? error.message : 'Could not delete the user. Try again.';
            showToast({
                show: true,
                message: errorMessage,
                type: 'error',
            });
        }
    };

    const handleSyncUserData = async (userId: string) => {
        try {
            setSyncingDataUsers((prev) => new Set(prev).add(userId));

            // Extract the database user public_id from the user object
            // The sync endpoint expects the user's public_id (database identifier)
            const user = orgUsers.find((u) => u.user_id === userId);
            const userPublicId = user?.public_id;

            if (!userPublicId) {
                throw new Error('Could not register this user because their user ID is missing.');
            }

            const result = await syncUserDataMutation.mutateAsync(userPublicId);

            // Show success toast with user info
            const teams = result.user.team_assignment?.teams || [];
            const firstTeam = teams[0];
            const teamName =
                !firstTeam?.name || firstTeam.name === ''
                    ? 'All Users'
                    : firstTeam?.name || 'No team';
            const companyName = firstTeam?.company?.name || 'No company';
            const companySyncNote = result.sync_result.company_was_synced
                ? ' (company synced)'
                : '';

            // Show team count if multiple teams
            const teamDisplayName =
                teams.length > 1
                    ? `${teamName} (+${teams.length - 1} more ${pluralize(
                          teams.length - 1,
                          'team',
                          'teams',
                      )})`
                    : teamName;

            showToast({
                show: true,
                message: `User registered: ${result.user.first_name} ${result.user.last_name} - ${teamDisplayName} at ${companyName}${companySyncNote}`,
                type: 'success',
            });

            // Auto-hide toast after 3 seconds

            console.log('User data sync result:', result);

            // Wait a moment for the sync to propagate, then refresh the user list
            setTimeout(async () => {
                try {
                    setIsRefreshingAfterSync(true);
                    console.log('Refreshing user list after sync...');
                    await refreshOrgUsers();
                    console.log('User list refreshed successfully');
                } catch (refreshError) {
                    console.error('Failed to refresh user list after sync:', refreshError);
                } finally {
                    setIsRefreshingAfterSync(false);
                }
            }, 1000); // 1 second delay
        } catch (error) {
            console.error('Failed to sync user data:', error);
            const errorMessage =
                error instanceof Error ? error.message : 'Could not register this user. Try again.';
            showToast({
                show: true,
                message: errorMessage,
                type: 'error',
            });
        } finally {
            setSyncingDataUsers((prev) => {
                const newSet = new Set(prev);
                newSet.delete(userId);
                return newSet;
            });
        }
    };

    // Only show this component if user has administrate:organizations or administrate:users permission
    if (!hasPermission('administrate:organizations') && !hasPermission('administrate:users')) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle as="h1" className={overlineClassName}>
                            Organization teams
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <LoadingState />
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <Card>
                    <CardHeader className="bg-muted/40">
                        <CardTitle as="h1" className={overlineClassName}>
                            Organization teams
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <InlineAlert tone="error">
                            Couldn't load this organization's teams. Refresh the page to try again.
                        </InlineAlert>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Back Button and Organization Header */}
            <Card>
                <CardHeader className="bg-muted/40">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                            <h1 className={cn(pageTitleClassName, 'min-w-0')}>
                                {organization?.display_name || organization?.name}
                            </h1>
                            <div
                                className={cn(
                                    ADMIN_BADGE_BASE_CLASS,
                                    ADMIN_BADGE_NEUTRAL_CLASS,
                                    'tabular-nums',
                                )}
                            >
                                {orgUsers.length}
                                <span className="sr-only"> members</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(`/admin/${orgId}/sso`)}
                            >
                                <ShieldCheck aria-hidden />
                                Manage SSO
                            </Button>
                        </div>
                    </div>
                    <CardDescription>
                        Users organized by teams. The "All Users" team represents all users in the
                        organization (required). Named teams show additional team memberships.
                        Select a synced user to view details.
                        {isRefreshingAfterSync && (
                            <span className="ml-2 inline-flex items-center gap-1 text-muted-foreground">
                                <InlineSpinner label={null} />
                                Refreshing after sync…
                            </span>
                        )}
                    </CardDescription>
                    <div className="mt-4">
                        <Button
                            onClick={handleBulkRegister}
                            variant="outline"
                            size="sm"
                            className="max-w-full max-sm:h-auto max-sm:min-h-8 max-sm:whitespace-normal max-sm:py-1.5 max-sm:text-left"
                            disabled={unsyncedUsers.length === 0}
                        >
                            <RefreshCw aria-hidden />
                            Register all unsynced username and password users (
                            {unsyncedUsers.length})
                        </Button>
                    </div>
                </CardHeader>

                <CardContent className="p-0">
                    <div className="space-y-0">
                        {/* Teams */}
                        {teamsWithMembers.map((team) => (
                            <div key={team.id} className="border-b border-gray-100 last:border-b-0">
                                <div
                                    role="heading"
                                    aria-level={2}
                                    className="bg-[#f8f9fc] border-b border-gray-100"
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleTeam(team.id)}
                                        aria-expanded={expandedTeams.has(team.id)}
                                        aria-controls={`team-${team.id}-members`}
                                        aria-describedby={
                                            !team.name || team.name === ''
                                                ? 'all-users-team-help'
                                                : undefined
                                        }
                                        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-[#f0f2f8] transition-colors"
                                    >
                                        <div className="flex items-center gap-3">
                                            <ChevronRight
                                                aria-hidden
                                                className={cn(
                                                    'size-4 shrink-0 text-gray-500 transition-transform motion-reduce:transition-none',
                                                    expandedTeams.has(team.id) && 'rotate-90',
                                                )}
                                            />
                                            <div className="flex items-center gap-2">
                                                {(!team.name || team.name === '') && (
                                                    <Users
                                                        className="size-4 shrink-0 text-gray-600"
                                                        aria-hidden
                                                    />
                                                )}
                                                <span
                                                    className={`text-base font-semibold leading-snug ${
                                                        !team.name || team.name === ''
                                                            ? 'text-gray-800'
                                                            : 'text-gray-900'
                                                    }`}
                                                >
                                                    {!team.name || team.name === ''
                                                        ? 'All Users'
                                                        : team.name}
                                                </span>
                                                {(!team.name || team.name === '') && (
                                                    <TooltipProvider delayDuration={0}>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <div className="flex items-center">
                                                                    <Info
                                                                        className="size-4 cursor-help text-gray-500"
                                                                        aria-hidden
                                                                    />
                                                                </div>
                                                            </TooltipTrigger>
                                                            <TooltipContent className="max-w-xs">
                                                                <p className="text-sm">
                                                                    This represents all users in the
                                                                    organization. Every user must be
                                                                    in this team as it shows their
                                                                    relationship to the company.
                                                                    Users may also belong to
                                                                    additional named teams below.
                                                                </p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                                {(!team.name || team.name === '') && (
                                                    <span className="text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded font-medium">
                                                        Default
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className={cn(
                                                    ADMIN_BADGE_BASE_CLASS,
                                                    ADMIN_BADGE_NEUTRAL_CLASS,
                                                    'tabular-nums',
                                                )}
                                            >
                                                {team.members.length}
                                                <span className="sr-only"> members</span>
                                            </div>
                                            {(!team.name || team.name === '') && (
                                                <span id="all-users-team-help" className="sr-only">
                                                    This represents all users in the organization.
                                                    Every user must be in this team as it shows
                                                    their relationship to the company. Users may
                                                    also belong to additional named teams below.
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                </div>

                                {expandedTeams.has(team.id) && (
                                    <div
                                        id={`team-${team.id}-members`}
                                        className="border-t border-gray-100 bg-white"
                                    >
                                        {(!team.name || team.name === '') && (
                                            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
                                                <div className="flex items-start justify-between gap-4">
                                                    <p className="text-xs text-blue-800 flex-1">
                                                        <strong>All Users team:</strong> This team
                                                        represents all users in the organization.
                                                        Every user must be in this team as it shows
                                                        their relationship to the company. Users may
                                                        also belong to additional named teams shown
                                                        below.
                                                    </p>
                                                    {usersNeedingEmptyTeamRegistration.length >
                                                        0 && (
                                                        <Button
                                                            onClick={() =>
                                                                setConfirmAction({
                                                                    kind: 'register-to-team',
                                                                    count: usersNeedingEmptyTeamRegistration.length,
                                                                })
                                                            }
                                                            variant="destructiveOutline"
                                                            size="sm"
                                                            disabled={isRefreshingAfterSync}
                                                        >
                                                            <RefreshCw aria-hidden />
                                                            Register{' '}
                                                            {
                                                                usersNeedingEmptyTeamRegistration.length
                                                            }{' '}
                                                            user
                                                            {usersNeedingEmptyTeamRegistration.length !==
                                                            1
                                                                ? 's'
                                                                : ''}{' '}
                                                            to team
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        <Table stickyHeader className="w-full table-auto">
                                            <TableHeader className="max-lg:sr-only">
                                                <TableRow>
                                                    <TableHead>Member</TableHead>
                                                    <TableHead>Email</TableHead>
                                                    <TableHead className="lg:w-px lg:whitespace-nowrap">
                                                        Role
                                                    </TableHead>
                                                    <TableHead className="lg:w-px lg:whitespace-nowrap">
                                                        Status
                                                    </TableHead>
                                                    {canManageUsers && (
                                                        <TableHead className="text-left lg:w-px lg:whitespace-nowrap">
                                                            Role actions
                                                        </TableHead>
                                                    )}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {team.members
                                                    .sort((a, b) => {
                                                        // Sort synced users first
                                                        if (a.subject_id && !b.subject_id)
                                                            return -1;
                                                        if (!a.subject_id && b.subject_id) return 1;
                                                        return a.name.localeCompare(b.name);
                                                    })
                                                    .map((user: TeamMemberUser) => {
                                                        const hasNotLoggedIn = !user.subject_id;
                                                        return (
                                                            <TableRow
                                                                key={user.user_id}
                                                                className={cn(
                                                                    'max-lg:flex max-lg:flex-col max-lg:gap-2 max-lg:py-3',
                                                                    hasNotLoggedIn
                                                                        ? 'bg-neutral-50 [&>td]:!bg-transparent'
                                                                        : 'cursor-pointer',
                                                                )}
                                                                onClick={() =>
                                                                    handleUserClick(user)
                                                                }
                                                                title={
                                                                    hasNotLoggedIn
                                                                        ? 'User not yet synced with Auth0'
                                                                        : 'View user details'
                                                                }
                                                            >
                                                                <TableCell className="max-lg:block max-lg:px-4 max-lg:py-0 sm:max-lg:px-5">
                                                                    <div className="flex items-center gap-3">
                                                                        {renderUserAvatar(
                                                                            user.name,
                                                                            hasNotLoggedIn,
                                                                        )}
                                                                        <div className="flex-1 min-w-0">
                                                                            <button
                                                                                onClick={() =>
                                                                                    handleUserClick(
                                                                                        user,
                                                                                    )
                                                                                }
                                                                                className="font-medium text-sm text-neutral-900 hover:text-primary-600 transition-colors cursor-pointer text-left flex items-center gap-2"
                                                                                title="View user details"
                                                                            >
                                                                                {user.name}
                                                                                {isInvalidUser(
                                                                                    user,
                                                                                ) && (
                                                                                    <TooltipProvider
                                                                                        delayDuration={
                                                                                            0
                                                                                        }
                                                                                    >
                                                                                        <Tooltip>
                                                                                            <TooltipTrigger
                                                                                                asChild
                                                                                            >
                                                                                                <TriangleAlert
                                                                                                    className="size-4 shrink-0 cursor-help text-amber-600"
                                                                                                    role="img"
                                                                                                    aria-label="This user cannot be registered because they are not configured for SSO and do not have a password set."
                                                                                                />
                                                                                            </TooltipTrigger>
                                                                                            <TooltipContent>
                                                                                                <p>
                                                                                                    This
                                                                                                    user
                                                                                                    cannot
                                                                                                    be
                                                                                                    registered
                                                                                                    because
                                                                                                    they
                                                                                                    are
                                                                                                    not
                                                                                                    configured
                                                                                                    for
                                                                                                    SSO
                                                                                                    and
                                                                                                    do
                                                                                                    not
                                                                                                    have
                                                                                                    a
                                                                                                    password
                                                                                                    set.
                                                                                                </p>
                                                                                            </TooltipContent>
                                                                                        </Tooltip>
                                                                                    </TooltipProvider>
                                                                                )}
                                                                            </button>
                                                                            {/* Show other teams this user belongs to */}
                                                                            {user.team_memberships &&
                                                                                user
                                                                                    .team_memberships
                                                                                    .length > 1 && (
                                                                                    <div className="mt-1 flex flex-wrap gap-1 items-center">
                                                                                        <span className="text-xs text-gray-500">
                                                                                            Also in:
                                                                                        </span>
                                                                                        {user.team_memberships
                                                                                            .filter(
                                                                                                (
                                                                                                    m: TeamMembership,
                                                                                                ) =>
                                                                                                    m.team_id !==
                                                                                                    team.id,
                                                                                            )
                                                                                            .map(
                                                                                                (
                                                                                                    membership: TeamMembership,
                                                                                                ) => (
                                                                                                    <span
                                                                                                        key={
                                                                                                            membership.team_id
                                                                                                        }
                                                                                                        className={`text-xs px-1.5 py-0.5 rounded ${
                                                                                                            !membership.team_name ||
                                                                                                            membership.team_name ===
                                                                                                                ''
                                                                                                                ? 'text-gray-700 bg-gray-200 font-medium'
                                                                                                                : 'text-gray-600 bg-gray-100'
                                                                                                        }`}
                                                                                                        title={`Also in: ${membership.team_name || 'All Users'}`}
                                                                                                    >
                                                                                                        {membership.team_name ||
                                                                                                            'All Users'}
                                                                                                    </span>
                                                                                                ),
                                                                                            )}
                                                                                    </div>
                                                                                )}
                                                                        </div>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="max-lg:block max-lg:px-4 max-lg:py-0 sm:max-lg:px-5">
                                                                    <div className="text-sm text-neutral-600">
                                                                        {user.email}
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="max-lg:block max-lg:px-4 max-lg:py-0 sm:max-lg:px-5 lg:w-px lg:whitespace-nowrap">
                                                                    <RoleBadge
                                                                        role={getProRoleName(
                                                                            toNumber(
                                                                                user.current_team_role_id,
                                                                            ),
                                                                        )}
                                                                    />
                                                                </TableCell>
                                                                <TableCell className="max-lg:block max-lg:px-4 max-lg:py-0 sm:max-lg:px-5 lg:w-px lg:whitespace-nowrap">
                                                                    <div className="text-xs">
                                                                        {hasNotLoggedIn ? (
                                                                            <span className="inline-flex items-center gap-1 text-neutral-600">
                                                                                <CircleDashed
                                                                                    className="size-3.5 shrink-0"
                                                                                    aria-hidden
                                                                                />
                                                                                Legacy
                                                                            </span>
                                                                        ) : (
                                                                            <span className="inline-flex items-center gap-1 text-green-700">
                                                                                <CircleCheck
                                                                                    className="size-3.5 shrink-0"
                                                                                    aria-hidden
                                                                                />
                                                                                Active
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </TableCell>
                                                                {canManageUsers && (
                                                                    <TableCell className="max-lg:block max-lg:px-4 max-lg:py-0 sm:max-lg:px-5 lg:w-px lg:whitespace-nowrap">
                                                                        <div className="flex flex-wrap items-center justify-start gap-1 lg:flex-nowrap">
                                                                            {hasNotLoggedIn &&
                                                                            user.is_sso === true ? (
                                                                                <TooltipProvider
                                                                                    delayDuration={
                                                                                        0
                                                                                    }
                                                                                >
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger
                                                                                            asChild
                                                                                        >
                                                                                            <span
                                                                                                tabIndex={
                                                                                                    0
                                                                                                }
                                                                                                role="img"
                                                                                                aria-label="User will be associated on next single sign on login."
                                                                                                className="inline-flex cursor-help rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                                                                            >
                                                                                                <Clock
                                                                                                    className="size-4 text-primary-500"
                                                                                                    aria-hidden
                                                                                                />
                                                                                            </span>
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            <p>
                                                                                                User
                                                                                                will
                                                                                                be
                                                                                                associated
                                                                                                on
                                                                                                next
                                                                                                single
                                                                                                sign
                                                                                                on
                                                                                                login.
                                                                                            </p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            ) : hasNotLoggedIn &&
                                                                              !isInvalidUser(
                                                                                  user,
                                                                              ) ? (
                                                                                <div className="flex flex-row gap-2">
                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="destructiveOutline"
                                                                                        size="sm"
                                                                                        loading={syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        )}
                                                                                        onClick={(
                                                                                            e,
                                                                                        ) => {
                                                                                            e.preventDefault();
                                                                                            setConfirmAction(
                                                                                                {
                                                                                                    kind: 'register-user',
                                                                                                    userId: user.user_id,
                                                                                                    userLabel:
                                                                                                        user.name ||
                                                                                                        user.email,
                                                                                                },
                                                                                            );
                                                                                            e.stopPropagation();
                                                                                        }}
                                                                                        disabled={syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        )}
                                                                                        title={
                                                                                            syncingDataUsers.has(
                                                                                                user.user_id,
                                                                                            )
                                                                                                ? 'Registering user…'
                                                                                                : 'Register user'
                                                                                        }
                                                                                        aria-label={
                                                                                            syncingDataUsers.has(
                                                                                                user.user_id,
                                                                                            )
                                                                                                ? `Registering ${user.name || user.email}`
                                                                                                : `Register ${user.name || user.email}`
                                                                                        }
                                                                                    >
                                                                                        {!syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        ) && (
                                                                                            <RefreshCw
                                                                                                aria-hidden
                                                                                            />
                                                                                        )}
                                                                                        {syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        )
                                                                                            ? 'Registering…'
                                                                                            : 'Register'}
                                                                                    </Button>
                                                                                    <Button
                                                                                        type="button"
                                                                                        variant="destructiveOutline"
                                                                                        size="sm"
                                                                                        onClick={(
                                                                                            e,
                                                                                        ) => {
                                                                                            e.preventDefault();
                                                                                            handleDeleteUser(
                                                                                                user.user_id,
                                                                                            );
                                                                                            e.stopPropagation();
                                                                                        }}
                                                                                        title="Delete user"
                                                                                    >
                                                                                        <Trash2
                                                                                            aria-hidden
                                                                                        />
                                                                                        Delete
                                                                                    </Button>
                                                                                </div>
                                                                            ) : hasNotLoggedIn &&
                                                                              isInvalidUser(
                                                                                  user,
                                                                              ) ? (
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="destructiveOutline"
                                                                                    size="sm"
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.preventDefault();
                                                                                        handleDeleteUser(
                                                                                            user.user_id,
                                                                                        );
                                                                                        e.stopPropagation();
                                                                                    }}
                                                                                    title="Delete user"
                                                                                >
                                                                                    <Trash2
                                                                                        aria-hidden
                                                                                    />
                                                                                    Delete
                                                                                </Button>
                                                                            ) : (
                                                                                <>
                                                                                    <div
                                                                                        role="group"
                                                                                        aria-label={`Team role for ${user.name || user.email}`}
                                                                                        className={
                                                                                            ROLE_TOGGLE_GROUP_CLASS
                                                                                        }
                                                                                    >
                                                                                        {PRO_TEAM_ROLE_BUTTONS.map(
                                                                                            (
                                                                                                roleButton,
                                                                                            ) => (
                                                                                                <TeamMemberRoleButton
                                                                                                    key={
                                                                                                        roleButton.roleId
                                                                                                    }
                                                                                                    teamId={
                                                                                                        team.id
                                                                                                    }
                                                                                                    user={
                                                                                                        user
                                                                                                    }
                                                                                                    roleId={
                                                                                                        roleButton.roleId
                                                                                                    }
                                                                                                    label={
                                                                                                        roleButton.label
                                                                                                    }
                                                                                                    title={
                                                                                                        roleButton.title
                                                                                                    }
                                                                                                    loadingStates={
                                                                                                        loadingStates
                                                                                                    }
                                                                                                    getMemberLoadingKey={
                                                                                                        getMemberLoadingKey
                                                                                                    }
                                                                                                    onSetRole={
                                                                                                        handleSetRole
                                                                                                    }
                                                                                                    hasNotLoggedIn={
                                                                                                        hasNotLoggedIn
                                                                                                    }
                                                                                                />
                                                                                            ),
                                                                                        )}
                                                                                    </div>

                                                                                    {user.subject_id ? (
                                                                                        <Button
                                                                                            type="button"
                                                                                            variant="destructiveOutline"
                                                                                            size="icon"
                                                                                            aria-label={`Remove ${user.name} from ${team.name || 'All Users'}`}
                                                                                            onClick={(
                                                                                                event,
                                                                                            ) => {
                                                                                                event.preventDefault();
                                                                                                handleRemoveUser(
                                                                                                    team.id,
                                                                                                    (user.subject_id ||
                                                                                                        user.user_id) as string,
                                                                                                    user.name,
                                                                                                );
                                                                                                event.stopPropagation();
                                                                                            }}
                                                                                            disabled={
                                                                                                loadingStates.removeUser ||
                                                                                                !team.name ||
                                                                                                team.name ===
                                                                                                    '' ||
                                                                                                (() => {
                                                                                                    const fullUser =
                                                                                                        orgUsers.find(
                                                                                                            (
                                                                                                                u,
                                                                                                            ) =>
                                                                                                                u.user_id ===
                                                                                                                    (user.subject_id ||
                                                                                                                        user.user_id) ||
                                                                                                                u.subject_id ===
                                                                                                                    (user.subject_id ||
                                                                                                                        user.user_id),
                                                                                                        );
                                                                                                    return (
                                                                                                        fullUser
                                                                                                            ?.team_memberships
                                                                                                            ?.length ===
                                                                                                        1
                                                                                                    );
                                                                                                })()
                                                                                            }
                                                                                            loading={
                                                                                                loadingStates.removeUser
                                                                                            }
                                                                                            className="ml-2 size-8"
                                                                                            title={(() => {
                                                                                                if (
                                                                                                    !team.name ||
                                                                                                    team.name ===
                                                                                                        ''
                                                                                                ) {
                                                                                                    return 'Cannot remove user from "All Users" team - this is required';
                                                                                                }
                                                                                                const fullUser =
                                                                                                    orgUsers.find(
                                                                                                        (
                                                                                                            u,
                                                                                                        ) =>
                                                                                                            u.user_id ===
                                                                                                                (user.subject_id ||
                                                                                                                    user.user_id) ||
                                                                                                            u.subject_id ===
                                                                                                                (user.subject_id ||
                                                                                                                    user.user_id),
                                                                                                    );
                                                                                                return fullUser
                                                                                                    ?.team_memberships
                                                                                                    ?.length ===
                                                                                                    1
                                                                                                    ? 'Cannot remove user from their only team'
                                                                                                    : 'Remove from team';
                                                                                            })()}
                                                                                        >
                                                                                            {!loadingStates.removeUser && (
                                                                                                <UserMinus
                                                                                                    className="size-4"
                                                                                                    aria-hidden
                                                                                                />
                                                                                            )}
                                                                                        </Button>
                                                                                    ) : (
                                                                                        <div className="p-1.5 w-[30px] h-[30px]" />
                                                                                    )}
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        );
                                                    })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Users not in any teams */}
                        {usersNotInAnyTeams.length > 0 && (
                            <div className="border-b border-gray-100 last:border-b-0">
                                <div className="bg-blue-50 border-b border-blue-200">
                                    <div className="px-4 py-3 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <ChevronRight
                                                className="size-4 shrink-0 text-blue-500"
                                                aria-hidden
                                            />
                                            <h2 className="text-base font-semibold leading-snug text-blue-900">
                                                Users not in any teams
                                            </h2>
                                            <div
                                                className={cn(
                                                    ADMIN_BADGE_BASE_CLASS,
                                                    ADMIN_BADGE_NEUTRAL_CLASS,
                                                    'tabular-nums',
                                                )}
                                            >
                                                {usersNotInAnyTeams.length}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-blue-200 bg-blue-25">
                                    <Table stickyHeader className="w-full table-auto">
                                        <TableHeader className="max-lg:sr-only">
                                            <TableRow>
                                                <TableHead className="px-4">User</TableHead>
                                                <TableHead className="px-4">Email</TableHead>
                                                <TableHead className="px-4 lg:w-px lg:whitespace-nowrap">
                                                    Role
                                                </TableHead>
                                                <TableHead className="px-4 lg:w-px lg:whitespace-nowrap">
                                                    Status
                                                </TableHead>
                                                {canManageUsers && (
                                                    <TableHead className="px-4 text-left lg:w-px lg:whitespace-nowrap">
                                                        Actions
                                                    </TableHead>
                                                )}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {usersNotInAnyTeams
                                                .sort((a, b) => {
                                                    // Sort synced users first
                                                    if (a.subject_id && !b.subject_id) return -1;
                                                    if (!a.subject_id && b.subject_id) return 1;
                                                    return a.name.localeCompare(b.name);
                                                })
                                                .map((user: EnhancedAuth0User) => {
                                                    const hasNotLoggedIn = !user.subject_id;
                                                    return (
                                                        <TableRow
                                                            key={user.user_id}
                                                            className={cn(
                                                                'max-lg:flex max-lg:flex-col max-lg:gap-2 max-lg:py-3',
                                                                hasNotLoggedIn
                                                                    ? 'bg-neutral-50 [&>td]:!bg-transparent'
                                                                    : 'cursor-pointer',
                                                            )}
                                                            onClick={() => handleUserClick(user)}
                                                            title={
                                                                hasNotLoggedIn
                                                                    ? 'User not yet synced with Auth0'
                                                                    : 'View user details'
                                                            }
                                                        >
                                                            <TableCell className="px-4 py-3 max-lg:block max-lg:py-0 sm:max-lg:px-5">
                                                                <div className="flex items-center gap-3">
                                                                    {renderUserAvatar(
                                                                        user.name,
                                                                        hasNotLoggedIn,
                                                                    )}
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="font-medium text-sm text-gray-900 truncate flex items-center gap-2">
                                                                            {user.name}
                                                                            {isInvalidUser(
                                                                                user,
                                                                            ) && (
                                                                                <TooltipProvider
                                                                                    delayDuration={
                                                                                        0
                                                                                    }
                                                                                >
                                                                                    <Tooltip>
                                                                                        <TooltipTrigger
                                                                                            asChild
                                                                                        >
                                                                                            <TriangleAlert
                                                                                                className="size-4 shrink-0 cursor-help text-amber-600"
                                                                                                role="img"
                                                                                                aria-label="This user cannot be registered because they are not configured for SSO and do not have a password set."
                                                                                            />
                                                                                        </TooltipTrigger>
                                                                                        <TooltipContent>
                                                                                            <p>
                                                                                                This
                                                                                                user
                                                                                                cannot
                                                                                                be
                                                                                                registered
                                                                                                because
                                                                                                they
                                                                                                are
                                                                                                not
                                                                                                configured
                                                                                                for
                                                                                                SSO
                                                                                                and
                                                                                                do
                                                                                                not
                                                                                                have
                                                                                                a
                                                                                                password
                                                                                                set.
                                                                                            </p>
                                                                                        </TooltipContent>
                                                                                    </Tooltip>
                                                                                </TooltipProvider>
                                                                            )}
                                                                        </div>
                                                                        {hasNotLoggedIn && (
                                                                            <div className="text-xs text-neutral-600">
                                                                                Not synced
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="px-4 py-3 max-lg:block max-lg:py-0 sm:max-lg:px-5">
                                                                <div className="text-sm text-gray-600 truncate">
                                                                    {user.email}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="px-4 py-3 max-lg:block max-lg:py-0 sm:max-lg:px-5 lg:w-px lg:whitespace-nowrap">
                                                                <span className="text-xs text-neutral-500">
                                                                    —
                                                                </span>
                                                            </TableCell>
                                                            <TableCell className="px-4 py-3 max-lg:block max-lg:py-0 sm:max-lg:px-5 lg:w-px lg:whitespace-nowrap">
                                                                <div className="text-xs">
                                                                    {hasNotLoggedIn ? (
                                                                        <span className="inline-flex items-center gap-1 text-neutral-600">
                                                                            <CircleDashed
                                                                                className="size-3.5 shrink-0"
                                                                                aria-hidden
                                                                            />
                                                                            Legacy
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-amber-700">
                                                                            No team
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                            {canManageUsers && (
                                                                <TableCell className="px-4 py-3 max-lg:block max-lg:py-0 sm:max-lg:px-5 lg:w-px lg:whitespace-nowrap">
                                                                    <div className="flex items-center justify-center gap-2">
                                                                        {hasNotLoggedIn &&
                                                                        user.is_sso === true ? (
                                                                            <TooltipProvider
                                                                                delayDuration={0}
                                                                            >
                                                                                <Tooltip>
                                                                                    <TooltipTrigger
                                                                                        asChild
                                                                                    >
                                                                                        <span
                                                                                            tabIndex={
                                                                                                0
                                                                                            }
                                                                                            role="img"
                                                                                            aria-label="User will be associated on next single sign on login."
                                                                                            className="inline-flex cursor-help rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                                                                        >
                                                                                            <Clock
                                                                                                className="size-4 text-primary-500"
                                                                                                aria-hidden
                                                                                            />
                                                                                        </span>
                                                                                    </TooltipTrigger>
                                                                                    <TooltipContent>
                                                                                        <p>
                                                                                            User
                                                                                            will be
                                                                                            associated
                                                                                            on next
                                                                                            single
                                                                                            sign on
                                                                                            login.
                                                                                        </p>
                                                                                    </TooltipContent>
                                                                                </Tooltip>
                                                                            </TooltipProvider>
                                                                        ) : hasNotLoggedIn &&
                                                                          !isInvalidUser(user) ? (
                                                                            <>
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="destructiveOutline"
                                                                                    size="sm"
                                                                                    loading={syncingDataUsers.has(
                                                                                        user.user_id,
                                                                                    )}
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.preventDefault();
                                                                                        setConfirmAction(
                                                                                            {
                                                                                                kind: 'register-user',
                                                                                                userId: user.user_id,
                                                                                                userLabel:
                                                                                                    user.name ||
                                                                                                    user.email,
                                                                                            },
                                                                                        );
                                                                                        e.stopPropagation();
                                                                                    }}
                                                                                    disabled={syncingDataUsers.has(
                                                                                        user.user_id,
                                                                                    )}
                                                                                    title={
                                                                                        syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        )
                                                                                            ? 'Registering user…'
                                                                                            : 'Register user'
                                                                                    }
                                                                                    aria-label={
                                                                                        syncingDataUsers.has(
                                                                                            user.user_id,
                                                                                        )
                                                                                            ? `Registering ${user.name || user.email}`
                                                                                            : `Register ${user.name || user.email}`
                                                                                    }
                                                                                >
                                                                                    {!syncingDataUsers.has(
                                                                                        user.user_id,
                                                                                    ) && (
                                                                                        <RefreshCw
                                                                                            aria-hidden
                                                                                        />
                                                                                    )}
                                                                                    {syncingDataUsers.has(
                                                                                        user.user_id,
                                                                                    )
                                                                                        ? 'Registering…'
                                                                                        : 'Register'}
                                                                                </Button>
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="destructiveOutline"
                                                                                    size="sm"
                                                                                    onClick={(
                                                                                        e,
                                                                                    ) => {
                                                                                        e.preventDefault();
                                                                                        handleDeleteUser(
                                                                                            user.user_id,
                                                                                        );
                                                                                        e.stopPropagation();
                                                                                    }}
                                                                                    title="Delete user"
                                                                                >
                                                                                    <Trash2
                                                                                        aria-hidden
                                                                                    />
                                                                                    Delete
                                                                                </Button>
                                                                            </>
                                                                        ) : hasNotLoggedIn &&
                                                                          isInvalidUser(user) ? (
                                                                            <Button
                                                                                type="button"
                                                                                variant="destructiveOutline"
                                                                                size="sm"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleDeleteUser(
                                                                                        user.user_id,
                                                                                    );
                                                                                    e.stopPropagation();
                                                                                }}
                                                                                title="Delete user"
                                                                            >
                                                                                <Trash2
                                                                                    aria-hidden
                                                                                />
                                                                                Delete
                                                                            </Button>
                                                                        ) : (
                                                                            <span className="text-xs text-neutral-500">
                                                                                —
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </TableCell>
                                                            )}
                                                        </TableRow>
                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        )}

                        {/* Empty state */}
                        {teamsWithMembers.length === 0 && usersNotInAnyTeams.length === 0 && (
                            <div className="p-6 text-center text-gray-600">
                                No users found in this organization.
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <AdminToast
                toast={toast}
                onClose={() => {
                    clearToastTimer();
                    setToast(emptyAdminToast);
                }}
            />
            <ConfirmDialog
                open={confirmAction !== null}
                title={
                    confirmAction?.kind === 'remove-from-team'
                        ? `Remove "${confirmAction.userName}" from "${confirmAction.teamName}" team?`
                        : confirmAction?.kind === 'delete-user'
                          ? `Delete ${confirmAction.userLabel}?`
                          : confirmAction?.kind === 'register-user'
                            ? `Register ${confirmAction.userLabel}?`
                            : `Register ${confirmAction?.count ?? 0} ${pluralize(
                                  confirmAction?.count ?? 0,
                                  'user',
                                  'users',
                              )} to team?`
                }
                description={
                    confirmAction?.kind === 'delete-user'
                        ? 'This will deactivate the user and they will no longer appear in the list.'
                        : undefined
                }
                confirmLabel={
                    confirmAction?.kind === 'remove-from-team'
                        ? 'Remove from team'
                        : confirmAction?.kind === 'delete-user'
                          ? 'Delete user'
                          : confirmAction?.kind === 'register-user'
                            ? 'Register user'
                            : `Register ${pluralize(
                                  confirmAction?.kind === 'register-to-team'
                                      ? confirmAction.count
                                      : 0,
                                  'user',
                                  'users',
                              )}`
                }
                pendingLabel={
                    confirmAction?.kind === 'remove-from-team'
                        ? 'Removing…'
                        : confirmAction?.kind === 'delete-user'
                          ? 'Deleting…'
                          : 'Registering…'
                }
                destructive
                onCancel={() => setConfirmAction(null)}
                onConfirm={async () => {
                    const action = confirmAction;
                    if (!action) {
                        return;
                    }
                    if (action.kind === 'remove-from-team') {
                        await removeUserFromTeamConfirmed(action.teamId, action.userId);
                        setConfirmAction(null);
                    } else if (action.kind === 'delete-user') {
                        await deleteUserConfirmed(action.userId);
                        setConfirmAction(null);
                    } else if (action.kind === 'register-user') {
                        await handleSyncUserData(action.userId);
                        setConfirmAction(null);
                    } else {
                        await handleRegisterToEmptyTeam();
                        setConfirmAction(null);
                    }
                }}
            />

            <DialogFrame
                open={showBulkRegisterModal}
                title="Register all unsynced username and password users"
                onClose={handleCloseBulkRegisterModal}
                closeDisabled={bulkRegisterStatus.isRunning}
                panelClassName="max-w-md"
            >
                <div className="space-y-4">
                    {!bulkRegisterStatus.isRunning && bulkRegisterStatus.registeredUsers === 0 && (
                        <div className="text-center py-4">
                            <p className="text-sm text-muted-foreground mb-0">
                                Found {bulkRegisterStatus.totalUsers || unsyncedUsers.length}{' '}
                                {pluralize(
                                    bulkRegisterStatus.totalUsers || unsyncedUsers.length,
                                    'user',
                                    'users',
                                )}{' '}
                                to register.
                            </p>
                        </div>
                    )}

                    {bulkRegisterStatus.isRunning && (
                        <div>
                            <div className="flex justify-between text-sm tabular-nums text-gray-600 mb-1">
                                <span>
                                    Progress: {bulkRegisterStatus.registeredUsers} /{' '}
                                    {bulkRegisterStatus.totalUsers}
                                </span>
                                <span>
                                    {bulkRegisterStatus.totalUsers > 0
                                        ? Math.round(
                                              (bulkRegisterStatus.registeredUsers /
                                                  bulkRegisterStatus.totalUsers) *
                                                  100,
                                          )
                                        : 0}
                                    %
                                </span>
                            </div>
                            <div
                                role="progressbar"
                                aria-label="Registration progress"
                                aria-valuemin={0}
                                aria-valuemax={bulkRegisterStatus.totalUsers}
                                aria-valuenow={bulkRegisterStatus.registeredUsers}
                                className="w-full bg-gray-200 rounded-full h-2"
                            >
                                <div
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{
                                        width: `${
                                            bulkRegisterStatus.totalUsers > 0
                                                ? Math.round(
                                                      (bulkRegisterStatus.registeredUsers /
                                                          bulkRegisterStatus.totalUsers) *
                                                          100,
                                                  )
                                                : 0
                                        }%`,
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div role="status" aria-live="polite">
                        {bulkRegisterStatus.currentUser && (
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                                    <span className="text-sm font-medium tabular-nums text-blue-800">
                                        Registering {bulkRegisterStatus.currentUser} (
                                        {bulkRegisterStatus.registeredUsers + 1} of{' '}
                                        {bulkRegisterStatus.totalUsers})
                                    </span>
                                </div>
                            </div>
                        )}

                        {!bulkRegisterStatus.isRunning &&
                            bulkRegisterStatus.totalUsers > 0 &&
                            bulkRegisterStatus.registeredUsers > 0 && (
                                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                                    <p className="text-sm text-green-800">
                                        Registration completed! {bulkRegisterStatus.registeredUsers}{' '}
                                        of {bulkRegisterStatus.totalUsers}{' '}
                                        {pluralize(bulkRegisterStatus.totalUsers, 'user', 'users')}{' '}
                                        registered successfully.
                                    </p>
                                </div>
                            )}
                    </div>

                    {bulkRegisterStatus.errors.length > 0 && (
                        <div
                            role="alert"
                            className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md"
                        >
                            <h4 className="text-sm font-semibold text-red-800 mb-1">
                                Errors ({bulkRegisterStatus.errors.length}):
                            </h4>
                            <ul className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
                                {bulkRegisterStatus.errors.slice(-5).map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    {bulkRegisterStatus.isRunning ? (
                        <Button onClick={handleCancelBulkRegister} variant="outline">
                            Cancel
                        </Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={handleCloseBulkRegisterModal}>
                                Close
                            </Button>
                            {bulkRegisterStatus.registeredUsers === 0 ? (
                                <Button onClick={handleStartBulkRegister}>
                                    Start registration
                                </Button>
                            ) : null}
                        </>
                    )}
                </DialogFooter>
            </DialogFrame>
        </div>
    );
}
